import { useRef, useEffect, ReactNode } from 'react'
import { ProductGroup as TProductGroup } from 'shared/types/api'
import { useVenueContext } from '@/services/venue'
import { webAppMobileWidth } from 'shared/misc'

export type ProductGroupProps<T extends TProductGroup> = {
  data: T
  intersectionObserver?: IntersectionObserver
  children: (product: T['products'][0], idx: number) => ReactNode | null
}

export function ProductGroup<T extends TProductGroup = TProductGroup>(props: ProductGroupProps<T>) {
  const { admin, venue } = useVenueContext()!
  const { isAdminView } = admin

  const rootBlock = useRef<HTMLDivElement>(null)
  useEffect(() => {
    props.intersectionObserver && props.intersectionObserver.observe(rootBlock.current!)
  }, [props.intersectionObserver])
  props.children

  const titleWords = props.data.title.split(' ')
  const titleFirstWords = titleWords.slice(0, titleWords.length - 1)
  const titleLastWord = titleWords.slice(titleWords.length - 1, titleWords.length)

  const showTwoProductsInRow = venue.features.showTwoProductsInRow

  return (
    <div
      ref={rootBlock}
      data-product-group-name={props.data.name}
      id={`pg-${props.data.name}`}
      className="flex flex-col mx-auto pb-2"
      style={{
        maxWidth: isAdminView ? webAppMobileWidth : '100%',
      }}
    >
      <div
        className={`mt-5 ${
          !isAdminView ? 'mx-auto w-full max-w-webAppMobileWidth md:max-w-[807px]' : ''
        }`}
      >
        <h2 className="px-4 font-medium text-2xl leading-none dark:text-primaryAccentDark">
          {titleFirstWords.join(' ')}{' '}
          <span className="whitespace-nowrap">
            {titleLastWord}{' '}
            <span className="w-2 font-medium text-opaque dark:text-opaqueDark text-lg">
              {props.data.products.length}
            </span>
          </span>
        </h2>
      </div>
      {props.data.descriptionShort ? (
        <div
          className={`px-4 text-opaque dark:text-opaqueDark leading-tight whitespace-pre-line ${
            !isAdminView ? 'mx-auto w-full lg:max-w-[807px]' : ''
          }`}
        >
          {props.data.descriptionShort}
        </div>
      ) : null}
      <div
        className={`grid ${showTwoProductsInRow && !isAdminView ? 'grid-cols-2' : 'grid-cols-1'} ${
          !isAdminView ? 'lg:grid-cols-2' : ''
        }`}
      >
        {props.data.products.map((product, idx) => {
          return (
            <div
              className={`w-full mx-auto ${
                !isAdminView ? 'lg:max-w-webAppMenuDesktopProductWidth' : ''
              }`}
              key={product.name}
            >
              {props.children(product, idx)}
            </div>
          )
        })}
      </div>
    </div>
  )
}
